import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Upload,
  MapPin,
  Camera,
  Send,
  AlertTriangle,
  Flame,
  Droplets,
  Mountain,
  Building2,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Image as ImageIcon,
  Loader,
  Navigation
} from 'lucide-react';

interface IncidentReport {
  id: string;
  type: 'flood' | 'fire' | 'collapse' | 'medical' | 'other';
  location: string;
  coordinates?: { lat: number; lng: number };
  description: string;
  timestamp: string;
  reporter: {
    name?: string;
    contact?: string;
    anonymous: boolean;
  };
  photos?: string[];
  status: 'pending' | 'verifying' | 'verified' | 'rejected';
  priority: 'high' | 'medium' | 'low';
  peopleAffected?: number;
}

const IncidentReportPortal: React.FC = () => {
  const [reports, setReports] = useState<IncidentReport[]>([
    {
      id: '1',
      type: 'flood',
      location: 'Zone A, Sector 7, Near River Bridge',
      description: 'Water level rising rapidly. Multiple families trapped on rooftops. Need immediate boat rescue.',
      timestamp: '5 min ago',
      reporter: { anonymous: true },
      status: 'verified',
      priority: 'high',
      peopleAffected: 47
    },
    {
      id: '2',
      type: 'fire',
      location: 'Zone B, Market Area, Building #23',
      description: 'Fire spreading through market complex. Smoke visible from distance. People evacuating.',
      timestamp: '12 min ago',
      reporter: { name: 'Rajesh Kumar', contact: '+91*****2345', anonymous: false },
      status: 'verifying',
      priority: 'high',
      peopleAffected: 12
    },
    {
      id: '3',
      type: 'collapse',
      location: 'Zone C, Residential Area, Block D',
      description: 'Building partially collapsed. Heard loud crack before fall. Possible people trapped.',
      timestamp: '25 min ago',
      reporter: { anonymous: true },
      status: 'pending',
      priority: 'medium',
      peopleAffected: 3
    }
  ]);

  const [showReportForm, setShowReportForm] = useState(false);
  const [formData, setFormData] = useState({
  type: 'flood' as const,
  location: '',
  description: '',
  name: '',
  contact: '',
  anonymous: true,
  priority: 'medium'  
});
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photos, setPhotos] = useState<string[]>([]);
  const [gettingLocation, setGettingLocation] = useState(false);

  const getTypeIcon = (type: string) => {
    switch(type) {
      case 'flood': return <Droplets size={16} className="text-blue-500" />;
      case 'fire': return <Flame size={16} className="text-orange-500" />;
      case 'collapse': return <Building2 size={16} className="text-red-500" />;
      case 'medical': return <Users size={16} className="text-green-500" />;
      default: return <AlertTriangle size={16} className="text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'verified': return 'text-green-500 border-green-500 bg-green-500/10';
      case 'verifying': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
      case 'pending': return 'text-orange-500 border-orange-500 bg-orange-500/10';
      case 'rejected': return 'text-red-500 border-red-500 bg-red-500/10';
      default: return 'text-gray-500';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'high': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const handleGetLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            location: `Lat: ${position.coords.latitude.toFixed(4)}, Lng: ${position.coords.longitude.toFixed(4)}`
          });
          setGettingLocation(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setGettingLocation(false);
        }
      );
    }
  };

  const handlePhotoUpload = () => {
    setUploadingPhotos(true);
    // Simulate photo upload
    setTimeout(() => {
      setPhotos([...photos, 'photo1.jpg', 'photo2.jpg']);
      setUploadingPhotos(false);
    }, 2000);
  };

  const handleSubmitReport = () => {
    const newReport: IncidentReport = {
      id: Date.now().toString(),
      type: formData.type,
      location: formData.location,
      description: formData.description,
      timestamp: 'Just now',
      reporter: {
        name: formData.anonymous ? undefined : formData.name,
        contact: formData.anonymous ? undefined : formData.contact,
        anonymous: formData.anonymous
      },
      photos: photos,
      status: 'pending',
        priority: formData.priority as 'high' | 'medium' | 'low'
    };

    setReports([newReport, ...reports]);
    setShowReportForm(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      type: 'flood',
      location: '',
      description: '',
      name: '',
      contact: '',
      anonymous: true,
      priority: 'medium'
    });
    setPhotos([]);
  };

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 holographic-card h-[600px] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="text-green-500 animate-pulse" />
            INCIDENT REPORTING PORTAL
          </h2>
          <p className="text-xs text-gray-400 mt-1">Public submission • Real-time verification</p>
        </div>

        <button
          onClick={() => setShowReportForm(true)}
          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
        >
          <Upload size={16} />
          NEW REPORT
        </button>
      </div>

      {/* Reports Feed */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scroll">
        <AnimatePresence>
          {reports.map((report, idx) => (
            <motion.div
              key={report.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-black/30 rounded-lg p-4 border border-gray-700 hover:border-cyan-500/50 transition-all"
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  {getTypeIcon(report.type)}
                  <span className="font-bold text-sm capitalize">{report.type} Incident</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(report.status)}`}>
                    {report.status}
                  </span>
                  <span className={`text-xs font-bold ${getPriorityColor(report.priority)}`}>
                    {report.priority.toUpperCase()} PRIORITY
                  </span>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
                <MapPin size={12} />
                {report.location}
              </div>

              {/* Description */}
              <p className="text-sm text-gray-300 mb-3">"{report.description}"</p>

              {/* Metadata */}
              <div className="flex items-center justify-between text-xs text-gray-400">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <Clock size={10} />
                    {report.timestamp}
                  </div>
                  {report.peopleAffected && (
                    <div className="flex items-center gap-1">
                      <Users size={10} />
                      {report.peopleAffected} affected
                    </div>
                  )}
                </div>
                <div className="text-[10px]">
                  {report.reporter.anonymous ? 'Anonymous' : report.reporter.name}
                </div>
              </div>

              {/* Photos */}
              {report.photos && report.photos.length > 0 && (
                <div className="flex gap-1 mt-2">
                  {report.photos.map((photo, i) => (
                    <div key={i} className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center">
                      <ImageIcon size={12} className="text-gray-400" />
                    </div>
                  ))}
                </div>
              )}

              {/* Action Buttons for Pending */}
              {report.status === 'pending' && (
                <div className="flex gap-2 mt-3">
                  <button className="flex-1 bg-green-600/20 hover:bg-green-600/30 text-green-500 text-xs font-bold py-2 px-3 rounded-lg border border-green-500/30">
                    Verify
                  </button>
                  <button className="flex-1 bg-red-600/20 hover:bg-red-600/30 text-red-500 text-xs font-bold py-2 px-3 rounded-lg border border-red-500/30">
                    Reject
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Report Form Modal */}
      <AnimatePresence>
        {showReportForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setShowReportForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-gray-900 w-96 p-6 rounded-xl border border-green-500/50"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-green-500 mb-4 flex items-center gap-2">
                <FileText size={20} />
                REPORT INCIDENT
              </h3>

              {/* Incident Type */}
              <div className="mb-4">
                <label className="text-xs text-gray-400 mb-1 block">Incident Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { type: 'flood', icon: Droplets, label: 'Flood' },
                    { type: 'fire', icon: Flame, label: 'Fire' },
                    { type: 'collapse', icon: Building2, label: 'Collapse' },
                    { type: 'medical', icon: Users, label: 'Medical' }
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.type}
                        onClick={() => setFormData({ ...formData, type: item.type as any })}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm ${
                          formData.type === item.type
                            ? 'bg-cyan-600 text-white'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        <Icon size={14} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Location */}
              <div className="mb-4">
                <label className="text-xs text-gray-400 mb-1 block">Location</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Enter location or use GPS"
                    className="flex-1 bg-black/50 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    onClick={handleGetLocation}
                    className="px-3 bg-gray-800 hover:bg-gray-700 rounded-lg"
                  >
                    {gettingLocation ? <Loader size={16} className="animate-spin" /> : <Navigation size={16} />}
                  </button>
                </div>
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="text-xs text-gray-400 mb-1 block">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the situation..."
                  rows={3}
                  className="w-full bg-black/50 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* Photo Upload */}
              <div className="mb-4">
                <button
                  onClick={handlePhotoUpload}
                  disabled={uploadingPhotos}
                  className="w-full bg-purple-600/20 hover:bg-purple-600/30 text-purple-500 border border-purple-500/30 rounded-lg py-3 flex items-center justify-center gap-2 text-sm font-bold"
                >
                  {uploadingPhotos ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      UPLOADING...
                    </>
                  ) : (
                    <>
                      <Camera size={16} />
                      UPLOAD PHOTOS
                    </>
                  )}
                </button>
                {photos.length > 0 && (
                  <div className="flex gap-1 mt-2">
                    {photos.map((photo, i) => (
                      <div key={i} className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center">
                        <ImageIcon size={12} className="text-gray-400" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reporter Info */}
              <div className="mb-4">
                <label className="flex items-center gap-2 text-sm mb-2">
                  <input
                    type="checkbox"
                    checked={formData.anonymous}
                    onChange={(e) => setFormData({ ...formData, anonymous: e.target.checked })}
                  />
                  Report Anonymously
                </label>

                {!formData.anonymous && (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your name"
                      className="w-full bg-black/50 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                    />
                    <input
                      type="text"
                      value={formData.contact}
                      onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                      placeholder="Contact (phone/email)"
                      className="w-full bg-black/50 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Priority */}
              <div className="mb-6">
                <label className="text-xs text-gray-400 mb-1 block">Priority Level</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['high', 'medium', 'low'] as const).map((p) => (
                    <button
                      key={p}
                      onClick={() => setFormData({ ...formData, priority: p })}
                      className={`py-2 px-3 rounded-lg text-sm font-bold uppercase ${
                        formData.priority === p
                          ? p === 'high' ? 'bg-red-600 text-white' :
                            p === 'medium' ? 'bg-yellow-600 text-white' : 'bg-green-600 text-white'
                          : 'bg-gray-800 text-gray-400'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleSubmitReport}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2"
                >
                  <Send size={16} />
                  SUBMIT REPORT
                </button>
                <button
                  onClick={() => setShowReportForm(false)}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 rounded-lg"
                >
                  CANCEL
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Footer */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-center pt-4 border-t border-gray-700/50">
        <div>
          <div className="text-2xl font-bold text-cyan-500">847</div>
          <div className="text-xs text-gray-400">Reports Today</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-green-500">623</div>
          <div className="text-xs text-gray-400">Verified</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-orange-500">124</div>
          <div className="text-xs text-gray-400">Pending</div>
        </div>
      </div>
    </div>
  );
};

export default IncidentReportPortal;